import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';

/**
 * A row of swatch-button radios for a single bundle option — whatever that
 * option is (Shade, Size, Color, ...). Reuses Horizon's native variant-swatch
 * markup, so keyboard navigation, focus, and selection all come from the
 * browser's native radio-group behavior rather than custom JS.
 *
 * Emits a bubbling `change` event when a value is chosen; read the chosen
 * value from the `value` attribute.
 *
 * In the `dropdown` style (`data-style="dropdown"`), the same radios sit inside
 * a popup panel opened from a trigger button, so each option can show its
 * swatch next to its name. The arrow keys move between options while the panel
 * is open, as in a native select. Enter, Escape, Tab, or a click closes it.
 *
 * @typedef {object} BundleOptionSelectRefs
 * @property {HTMLElement} valueLabel - The legend element showing the current selection.
 * @property {HTMLElement} [description] - Element showing the selected value's description, if any.
 * @property {HTMLButtonElement} [trigger] - Dropdown style: the button that opens the panel.
 * @property {HTMLElement} [panel] - Dropdown style: the popup holding the radios.
 * @property {HTMLElement} [currentSwatch] - Dropdown style: shows the chosen value's swatch on the trigger.
 * @extends Component<BundleOptionSelectRefs>
 */
class BundleOptionSelect extends Component {
  requiredRefs = ['valueLabel'];

  /** @type {AbortController | null} Document listeners active while the panel is open. */
  #openController = null;

  /** @type {boolean} Set by a pointer press in the panel, so that pick closes it (arrow keys don't). */
  #pointerPick = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.panel?.addEventListener('pointerdown', () => (this.#pointerPick = true));
    this.refs.panel?.addEventListener('click', this.#handlePanelClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#openController?.abort();
  }

  /** @returns {string} */
  get value() {
    return this.getAttribute('value') ?? '';
  }

  /** @returns {boolean} */
  get #isOpen() {
    return Boolean(this.refs.panel && !this.refs.panel.hidden);
  }

  toggle() {
    if (this.#isOpen) this.close();
    else this.open();
  }

  open() {
    const { panel, trigger } = this.refs;
    if (!panel || !trigger || this.#isOpen) return;

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    const inputs = /** @type {HTMLInputElement[]} */ ([...panel.querySelectorAll('input[type="radio"]')]);
    const current = inputs.find((input) => input.checked) ?? inputs.find((input) => !input.disabled);
    current?.focus({ preventScroll: true });
    current?.closest('label')?.scrollIntoView({ block: 'nearest' });

    this.#openController = new AbortController();
    const { signal } = this.#openController;
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.close({ focusTrigger: false });
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        // Clicking an option label can blur the radio (Safari) before the click lands.
        if (this.#pointerPick) return;
        if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.close({ focusTrigger: false });
      },
      { signal }
    );
  }

  /** @param {{ focusTrigger?: boolean }} [options] */
  close({ focusTrigger = true } = {}) {
    const { panel, trigger } = this.refs;
    if (!panel || !trigger || !this.#isOpen) return;

    this.#openController?.abort();
    this.#openController = null;
    this.#pointerPick = false;

    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) trigger.focus();
  }

  /** @param {KeyboardEvent} event */
  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
    }
  }

  /** @param {KeyboardEvent} event */
  handlePanelKeydown(event) {
    // A pointer press that didn't pick (e.g. dragging the scrollbar) mustn't make arrow moves close the panel.
    this.#pointerPick = false;
    // Enter on a radio would otherwise submit the form.
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Closes the panel after a pointer pick, including re-picking the value
   * that's already checked (which fires no `change`). Keyboard arrow moves
   * also fire `click` on the radio, which is why the pointer flag is needed.
   * @param {MouseEvent} event
   */
  #handlePanelClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !this.#pointerPick) return;
    this.#pointerPick = false;
    // Let the radio's change handler run first.
    requestAnimationFrame(() => this.close());
  };

  /**
   * Handles a change on one of the radio inputs. Placed directly on each
   * input (rather than delegated from a parent), so `event.target` is the
   * real input — no Horizon `on:` delegation proxy involved.
   * @param {Event & { target: HTMLInputElement }} event
   */
  handleChange(event) {
    const input = event.target;
    const { value } = input;

    this.setAttribute('value', value);
    this.refs.valueLabel.textContent = value;

    const currentSwatch = this.refs.currentSwatch;
    const swatch = input.closest('label')?.querySelector('.swatch');
    if (currentSwatch && swatch) {
      currentSwatch.replaceChildren(swatch.cloneNode(true));
      currentSwatch.hidden = false;
    }

    const description = this.refs.description;
    if (description) {
      const text = input.dataset.description ?? '';
      description.textContent = text;
      description.hidden = !text;
    }

    const image = input.dataset.image;
    if (image) {
      const rowImage = this.closest('.bundle-builder__item')?.querySelector('.bundle-builder__image');
      if (rowImage instanceof HTMLImageElement) {
        // Clear srcset first — a stale srcset takes precedence over a new src.
        rowImage.removeAttribute('srcset');
        rowImage.removeAttribute('sizes');
        rowImage.src = image;
        if (input.dataset.imageAlt) rowImage.alt = input.dataset.imageAlt;
      }
    }

    this.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('bundle-option-select')) {
  customElements.define('bundle-option-select', BundleOptionSelect);
}

/**
 * Resolves the selections across all bundle-option dropdowns to a single parent
 * bundle variant and adds that variant to the cart.
 *
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string | null} option1
 * @property {string | null} option2
 * @property {string | null} option3
 * @property {boolean} available
 * @property {number | null} mediaId - The variant's featured media id, if it has an image.
 *
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLInputElement} variantId - Hidden input holding the resolved variant id.
 * @property {HTMLButtonElement} addButton - The add to cart button.
 * @property {HTMLElement} [message] - Status / error message region.
 * @extends Component<BundleBuilderRefs>
 */
class BundleBuilderComponent extends Component {
  requiredRefs = ['variantId', 'addButton'];

  /** @type {BundleVariant[]} */
  #variants = [];

  /** @type {boolean} */
  #adding = false;

  /** @type {number | null} Featured media id currently shown first in the gallery. */
  #galleryMediaId = null;

  /** @type {AbortController | null} */
  #galleryAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#variants = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent ?? '[]');
    } catch (error) {
      console.warn('[bundle-builder] Could not parse variant data', error);
      this.#variants = [];
    }

    this.#sync();
  }

  /** @returns {HTMLElement[]} The bundle-option dropdowns, in DOM order. */
  get #selects() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('bundle-option-select')]);
  }

  /** Re-evaluates state whenever an option selection changes. */
  handleChange() {
    this.#sync();
    this.#updateGallery();
  }

  /**
   * Picks the bundle variant whose image best represents the swatches chosen so
   * far — selections can be partial, so this matches only the options picked
   * and prefers an available variant that has an image.
   * @returns {BundleVariant | null}
   */
  #galleryVariant() {
    /** @type {[string, string][]} */
    const picks = [];
    for (const select of this.#selects) {
      const value = select.getAttribute('value') ?? '';
      const key = select.dataset.option;
      if (value && key) picks.push([key, value]);
    }
    if (!picks.length) return null;

    const matches = this.#variants.filter(
      (candidate) =>
        candidate.mediaId &&
        picks.every(([key, value]) => candidate[/** @type {keyof BundleVariant} */ (key)] === value)
    );

    return matches.find((candidate) => candidate.available) ?? matches[0] ?? null;
  }

  /**
   * Swaps the product media gallery to the matching bundle variant's images by
   * fetching the section rendered for that variant and handing it to the
   * gallery through the theme's standard variant-update event.
   */
  async #updateGallery() {
    const variant = this.#galleryVariant();
    if (!variant || variant.mediaId === this.#galleryMediaId) return;

    const section = this.closest('.shopify-section');
    const productUrl = this.dataset.productUrl;
    if (!section || !productUrl) return;

    const sectionId = section.id.replace(/^shopify-section-/, '');

    this.#galleryAbortController?.abort();
    this.#galleryAbortController = new AbortController();

    try {
      const response = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`, {
        signal: this.#galleryAbortController.signal,
      });
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const mediaGallery = html.querySelector('media-gallery');
      if (!mediaGallery) return;

      this.#galleryMediaId = variant.mediaId;

      // `productId` deliberately doesn't match the page product, so price, SKU,
      // product form, etc. ignore this event and only the media gallery (which
      // doesn't filter by product) re-renders.
      section.dispatchEvent(
        new VariantUpdateEvent(
          { id: String(variant.id), available: variant.available, inventory_management: false },
          this.id,
          {
            html,
            productId: 'bundle-builder-gallery',
            mediaGalleryHtml: /** @type {Element} */ (mediaGallery.cloneNode(true)),
          }
        )
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /** @returns {{ complete: boolean, variant: BundleVariant | null }} */
  #resolve() {
    /** @type {Record<string, string>} */
    const picks = {};

    for (const select of this.#selects) {
      const value = select.getAttribute('value') ?? '';
      const key = select.dataset.option;
      if (!value || !key) return { complete: false, variant: null };
      picks[key] = value;
    }

    const variant =
      this.#variants.find((candidate) =>
        Object.entries(picks).every(([key, value]) => candidate[/** @type {keyof BundleVariant} */ (key)] === value)
      ) ?? null;

    return { complete: true, variant };
  }

  /** Updates the button + message + hidden input to reflect the current selection. */
  #sync() {
    const { complete, variant } = this.#resolve();
    const available = Boolean(variant && variant.available);
    const button = this.refs.addButton;

    if (available && variant) {
      this.refs.variantId.value = String(variant.id);
      button.disabled = false;
      button.textContent = this.dataset.addText ?? 'Add to cart';
      this.#setMessage('');
      return;
    }

    this.refs.variantId.value = '';
    button.disabled = true;
    button.textContent = complete ? (this.dataset.addText ?? 'Add to cart') : (this.dataset.incompleteText ?? 'Make your selections');
    this.#setMessage(complete ? (this.dataset.unavailableText ?? '') : '');
  }

  /**
   * Adds the resolved parent variant to the cart, mirroring Horizon's
   * product-form add flow so the cart drawer opens and refreshes.
   * @param {Event} event
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#adding) return;

    const id = this.refs.variantId.value;
    if (!id) return;

    this.#adding = true;
    const button = this.refs.addButton;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = /** @type {string[]} */ (
      [...document.querySelectorAll('cart-items-component')]
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
        .filter(Boolean)
    );

    const formData = new FormData();
    formData.set('id', id);
    formData.set('quantity', '1');
    const { propertyName, propertyValue } = this.dataset;
    if (propertyName && propertyValue) formData.set(`properties[${propertyName}]`, propertyValue);
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'bundle-builder-component',
            itemCount: 1,
          })
        );

        this.#setMessage(data.description || data.message || this.dataset.unavailableText || '');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, id, {
          source: 'bundle-builder-component',
          itemCount: 1,
          variantId: id,
          sections: data.sections,
        })
      );

      button.dataset.added = 'true';
      window.setTimeout(() => button.removeAttribute('data-added'), 2000);
    } catch (error) {
      console.error(error);
      this.#setMessage(this.dataset.unavailableText || '');
    } finally {
      this.#adding = false;
      button.removeAttribute('aria-busy');
      this.#sync();
    }
  }

  /** @param {string} text */
  #setMessage(text) {
    const element = this.refs.message;
    if (!element) return;
    element.textContent = text;
    element.hidden = !text;
  }
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}
